import {indentWithTab} from "@codemirror/commands";
import {python} from "@codemirror/lang-python";
import {indentUnit} from "@codemirror/language";
import {linter, lintGutter} from "@codemirror/lint";
import {Facet, type Extension} from "@codemirror/state";
import {EditorView, keymap} from "@codemirror/view";
import {basicSetup} from "codemirror";

import {doNotTranslateYet} from "@khan/i18n";

import {lightTheme} from "./code-editor-theme.tsx";

/**
 * Maps a (1-indexed) row + column pair provided by the Ruff linter to a
 * position in the document that CodeMirror understands.
 */
export const mapToPosition = (view: EditorView, {row, column}): number => {
    // CodeMirror's lines are 1-indexed, too, so this works without any transformation.
    const line = view.state.doc.line(row);
    // CodeMirror's columns are 0-indexed, however, so we need to adjust the start
    // position of the line by 1.
    return line.from - 1 + column;
};

/**
 * The FilenameFacet allows us to configure the EditorState with the name of the
 * file that it represents.
 */
export const FilenameFacet = Facet.define<string, string>({
    // In theory multiple instances of the facet value could be added to the
    // EditorState's extensions, but we don't expect that to happen, so we'll
    // always use the first one. Default to an empty string.
    combine: (values) => (values.length ? values[0] : ""),
});

export type Diagnostic = {
    message: string;
    severity: "hint" | "info" | "warning" | "error";
    start: {
        row: number;
        column: number;
    };
    end: {
        row: number;
        column: number;
    };
};

export const getLintExtension = (
    lintCode: (code: string) => Diagnostic[] | Promise<Diagnostic[]>,
): Extension => {
    return linter(async (view) => {
        const code = view.state.doc.toString();
        const result = await lintCode(code);
        const diagnostics = result.map((diagnostic) => {
            const from = mapToPosition(view, diagnostic.start);
            const to = mapToPosition(view, diagnostic.end);
            return {
                from,
                to,
                message: diagnostic.message,
                severity: diagnostic.severity,
            };
        });
        return diagnostics;
    });
};

export const getPythonExtensions = () => [
    basicSetup,
    python(),
    lintGutter(),
    lightTheme(),
    indentUnit.of("    "), // 4 spaces
    keymap.of([indentWithTab]), // enables indenting with the Tab key

    // Set the aria-label property on the editor so that we can tell screen reader
    // users how to escape the editor (because tab is used for indentation).
    EditorView.contentAttributes.of({
        "aria-label": doNotTranslateYet`Code editor. Use Escape + Tab to leave the editor.`,
    }),
];
